/* eslint-disable */
import userTimeKpiService from '@/services/userTimeKpiService';

/**
 * User Time KPI Store Module
 * Manages user activity and time tracking data
 */
export default {
  namespaced: true,

  state: {
    // User activity data mapped by user ID
    userActivityData: {},

    // Period-specific activity stats (for the dialog breakdown) mapped by cacheKey
    userActivityStats: {},

    // User chart data mapped by user ID and period
    userChartData: {},

    // Real-time stats mapped by user ID
    userRealTimeStats: {},

    // Loading states
    loading: {
      activity: false,
      chart: false,
      realTime: false,
      batch: false,
    },

    // Error states
    errors: {
      activity: null,
      chart: null,
      realTime: null,
      batch: null,
    },

    // Cache settings
    cache: {
      lastUpdated: null,
      cacheDuration: 5 * 60 * 1000, // 5 minutes
    },
  },

  getters: {
    /**
     * Get activity data for a specific user
     * @param {Object} state - Vuex state
     * @param {string} userId - User ID
     * @returns {Object|null} User activity data
     */
    getUserActivityData: (state) => (userId) => state.userActivityData[userId] || null,

    /**
     * Get chart data for a specific user and period
     * @param {Object} state - Vuex state
     * @param {string} userId - User ID
     * @param {string} periodType - Period type (daily, weekly, monthly)
     * @returns {Object|null} User chart data
     */
    getUserChartData: (state) => (userId, periodType = 'daily') => {
      // periodType can be a simple string like 'daily' or a full key like 'userId_custom_2025-01-01_2025-01-31'
      // If it's already a full key (starts with userId), use it directly; otherwise build the key
      let key = periodType;
      if (!periodType.startsWith(userId)) {
        key = `${userId}_${periodType}`;
      }
      return state.userChartData[key] || null;
    },

    /**
     * Get real-time stats for a specific user
     * @param {Object} state - Vuex state
     * @param {string} userId - User ID
     * @returns {Object|null} User real-time stats
     */
    getUserRealTimeStats: (state) => (userId) => state.userRealTimeStats[userId] || null,

    /**
     * Get period-specific activity stats for a user
     */
    getUserActivityStats: (state) => (userId, periodKey) => {
      const key = `${userId}_${periodKey}`;
      return state.userActivityStats[key] || null;
    },

    /**
     * Check if data is cached and still valid
     * @param {Object} state - Vuex state
     * @returns {boolean} Whether cache is valid
     */
    isCacheValid: (state) => {
      if (!state.cache.lastUpdated) return false;
      return Date.now() - state.cache.lastUpdated < state.cache.cacheDuration;
    },

    /**
     * Get loading state for a specific operation
     * @param {Object} state - Vuex state
     * @param {string} operation - Operation name
     * @returns {boolean} Loading state
     */
    isLoading: (state) => (operation) => state.loading[operation] || false,

    /**
     * Get error for a specific operation
     * @param {Object} state - Vuex state
     * @param {string} operation - Operation name
     * @returns {string|null} Error message
     */
    getError: (state) => (operation) => state.errors[operation] || null,
  },

  mutations: {
    /**
     * Set user activity data
     * @param {Object} state - Vuex state
     * @param {Object} payload - { userId, data }
     */
    SET_USER_ACTIVITY_DATA(state, { userId, data }) {
      state.userActivityData = {
        ...state.userActivityData,
        [userId]: data,
      };
    },

    /**
     * Set user chart data
     * @param {Object} state - Vuex state
     * @param {Object} payload - { userId, periodType, data }
     */
    SET_USER_CHART_DATA(state, { userId, periodType, data }) {
      const key = `${userId}_${periodType}`;
      state.userChartData = {
        ...state.userChartData,
        [key]: data,
      };
    },

    /**
     * Set user real-time stats
     * @param {Object} state - Vuex state
     * @param {Object} payload - { userId, data }
     */
    SET_USER_REAL_TIME_STATS(state, { userId, data }) {
      state.userRealTimeStats = {
        ...state.userRealTimeStats,
        [userId]: data,
      };
    },

    /**
     * Set multiple users activity data (batch operation)
     * @param {Object} state - Vuex state
     * @param {Object} userActivityMap - Map of userId to activity data
     */
    SET_MULTIPLE_USERS_ACTIVITY_DATA(state, userActivityMap) {
      // Only merge valid userId keys (skip null/undefined to avoid breaking the UI)
      const validMap = Object.fromEntries(
        Object.entries(userActivityMap || {}).filter(([key]) => key != null && key !== '')
      );
      state.userActivityData = {
        ...state.userActivityData,
        ...validMap,
      };
    },

    /**
     * Set period-specific activity stats for a user
     */
    SET_USER_ACTIVITY_STATS(state, { userId, periodKey, data }) {
      const key = `${userId}_${periodKey}`;
      state.userActivityStats = {
        ...state.userActivityStats,
        [key]: data,
      };
    },

    /**
     * Set loading state
     * @param {Object} state - Vuex state
     * @param {Object} payload - { operation, loading }
     */
    SET_LOADING(state, { operation, loading }) {
      state.loading = {
        ...state.loading,
        [operation]: loading,
      };
    },

    /**
     * Set error state
     * @param {Object} state - Vuex state
     * @param {Object} payload - { operation, error }
     */
    SET_ERROR(state, { operation, error }) {
      state.errors = {
        ...state.errors,
        [operation]: error,
      };
    },

    /**
     * Clear error for an operation
     * @param {Object} state - Vuex state
     * @param {string} operation - Operation name
     */
    CLEAR_ERROR(state, operation) {
      state.errors = {
        ...state.errors,
        [operation]: null,
      };
    },

    /**
     * Update cache timestamp
     * @param {Object} state - Vuex state
     */
    UPDATE_CACHE_TIMESTAMP(state) {
      state.cache.lastUpdated = Date.now();
    },

    /**
     * Clear all data (for logout or reset)
     * @param {Object} state - Vuex state
     */
    CLEAR_ALL_DATA(state) {
      state.userActivityData = {};
      state.userActivityStats = {};
      state.userChartData = {};
      state.userRealTimeStats = {};
      state.errors = {
        activity: null,
        chart: null,
        realTime: null,
        batch: null,
      };
      state.cache.lastUpdated = null;
    },
  },

  actions: {
    /**
     * Fetch user's last week activity
     * @param {Object} context - Vuex context
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Activity data
     */
    async fetchUserLastWeekActivity({ commit, getters }, userId) {
      // Check cache first
      if (getters.getUserActivityData(userId) && getters.isCacheValid) {
        return getters.getUserActivityData(userId);
      }

      commit('SET_LOADING', { operation: 'activity', loading: true });
      commit('CLEAR_ERROR', 'activity');

      try {
        const response = await userTimeKpiService.getUserLastWeekActivity(userId);

        if (response.success && response.data) {
          commit('SET_USER_ACTIVITY_DATA', { userId, data: response.data });
          commit('UPDATE_CACHE_TIMESTAMP');
        }

        return response.data;
      } catch (error) {
        commit('SET_ERROR', { operation: 'activity', error: error.message });
        throw error;
      } finally {
        commit('SET_LOADING', { operation: 'activity', loading: false });
      }
    },

    /**
     * Fetch user's activity chart data
     * @param {Object} context - Vuex context
     * @param {Object} payload - { userId, periodType, startDate, endDate }
     * @returns {Promise<Object>} Chart data
     */
    /**
     * Fetch user's real-time stats
     * @param {Object} context - Vuex context
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Real-time stats
     */
    async fetchUserRealTimeStats({ commit }, userId) {
      // Real-time data should not be cached
      commit('SET_LOADING', { operation: 'realTime', loading: true });
      commit('CLEAR_ERROR', 'realTime');

      try {
        const response = await userTimeKpiService.getUserRealTimeStats(userId);

        if (response.success && response.data) {
          commit('SET_USER_REAL_TIME_STATS', { userId, data: response.data });
        }

        return response.data;
      } catch (error) {
        commit('SET_ERROR', { operation: 'realTime', error: error.message });
        throw error;
      } finally {
        commit('SET_LOADING', { operation: 'realTime', loading: false });
      }
    },

    /**
     * Fetch period-specific activity stats for a user (for the dialog breakdown).
     * @param {Object} context - Vuex context
     * @param {Object} payload - { userId, periodType, startDate, endDate }
     * @returns {Promise<Object>} Activity stats
     */
    async fetchUserActivityStats({ commit }, { userId, periodType = 'weekly', startDate = null, endDate = null }) {
      try {
        const response = await userTimeKpiService.getUserActivityStats(userId, periodType, startDate, endDate);

        if (response.success && response.data) {
          const periodKey = startDate && endDate
            ? `custom_${startDate.toISOString()}_${endDate.toISOString()}`
            : periodType;
          commit('SET_USER_ACTIVITY_STATS', { userId, periodKey, data: response.data });
          return response.data;
        }
        return null;
      } catch (error) {
        console.error(`[userTimeKpi] Error fetching activity stats for ${userId}:`, error);
        return null;
      }
    },

    /**
     * Fetch multiple users' activity data for the last week (batch operation)
     * Returns activity-based data for determining if user is active
     * @param {Object} context - Vuex context
     * @param {Array<string>} userIds - Array of user IDs
     * @returns {Promise<Object>} Activity data map
     */
    async fetchMultipleUsersLastWeekActivity({ commit, getters }, userIds) {
      // Check if we have cached data for all users AND the data has the new format (hasActivity property)
      const hasAllCached = userIds.every((userId) => {
        const data = getters.getUserActivityData(userId);
        // Check for new format: hasActivity property must exist (not undefined)
        // hasActivity can be true or false, but it must be defined
        return data && typeof data.hasActivity === 'boolean' && getters.isCacheValid;
      });

      if (hasAllCached) {
        return userIds.reduce((acc, userId) => {
          acc[userId] = getters.getUserActivityData(userId);
          return acc;
        }, {});
      }

      commit('SET_LOADING', { operation: 'batch', loading: true });
      commit('CLEAR_ERROR', 'batch');

      try {
        const userActivityMap = await userTimeKpiService.getMultipleUsersLastWeekActivity(userIds);

        commit('SET_MULTIPLE_USERS_ACTIVITY_DATA', userActivityMap);
        commit('UPDATE_CACHE_TIMESTAMP');

        return userActivityMap;
      } catch (error) {
        commit('SET_ERROR', { operation: 'batch', error: error.message });
        throw error;
      } finally {
        commit('SET_LOADING', { operation: 'batch', loading: false });
      }
    },

    /**
     * Calculate and get KPI for a user
     * @param {Object} context - Vuex context
     * @param {Object} payload - { userId, periodType, startDate?, endDate? }
     * @returns {Promise<Object>} Calculated KPI data
     */
    async calculateAndGetUserKpi({ commit }, { userId, periodType = 'daily', startDate = null, endDate = null, updateActivityData = false }) {
      commit('SET_LOADING', { operation: 'activity', loading: true });
      commit('CLEAR_ERROR', 'activity');

      try {
        const response = await userTimeKpiService.calculateAndGetUserKpi(userId, periodType, startDate, endDate);

        if (response.success && response.data) {
          // Only update activity data if explicitly requested (for table view)
          // Otherwise, only update chart data to avoid overwriting batch API data
          if (updateActivityData) {
            commit('SET_USER_ACTIVITY_DATA', { userId, data: response.data.stored });
          }
          // Always update chart data for the specific period
          commit('SET_USER_CHART_DATA', { userId, periodType, data: response.data.calculation });
          commit('UPDATE_CACHE_TIMESTAMP');
        }

        return response.data;
      } catch (error) {
        commit('SET_ERROR', { operation: 'activity', error: error.message });
        throw error;
      } finally {
        commit('SET_LOADING', { operation: 'activity', loading: false });
      }
    },

    /**
     * Fetch user activity chart with custom date range
     * @param {Object} context - Vuex context
     * @param {Object} payload - { userId, periodType, startDate, endDate }
     * @returns {Promise<Object>} Chart data
     */
    async fetchUserActivityChart({ commit }, { userId, periodType = 'daily', startDate = null, endDate = null }) {
      commit('SET_LOADING', { operation: 'chart', loading: true });
      commit('CLEAR_ERROR', 'chart');

      try {
        const response = await userTimeKpiService.getUserActivityChart(userId, periodType, startDate, endDate);

        if (response.success && response.data) {
          // Update chart data with the custom date range
          // Build the periodType key (without userId prefix, as SET_USER_CHART_DATA will add it)
          const periodTypeKey = startDate && endDate 
            ? `custom_${startDate.toISOString()}_${endDate.toISOString()}`
            : periodType;
          
          commit('SET_USER_CHART_DATA', { 
            userId, 
            periodType: periodTypeKey, 
            data: response.data.calculation || response.data.stored 
          });
          commit('UPDATE_CACHE_TIMESTAMP');
        }

        return response.data;
      } catch (error) {
        commit('SET_ERROR', { operation: 'chart', error: error.message });
        throw error;
      } finally {
        commit('SET_LOADING', { operation: 'chart', loading: false });
      }
    },

    /**
     * Clear all data (for logout or reset)
     * @param {Object} context - Vuex context
     */
    clearAllData({ commit }) {
      commit('CLEAR_ALL_DATA');
    },
  },
};
